import {
  SITE_KEY_ACCOUNT_OPTIONS,
  SITE_MAKE_OPTIONS,
  SITE_TRUCK_MAKE_OPTIONS,
  SITE_SOURCE_OPTIONS,
  SITE_STATUS_OPTIONS,
  SITE_TRUCK_BODY_TYPE_OPTIONS,
  SITE_TRUCK_CAB_TYPE_OPTIONS,
  SITE_TRUCK_ENGINE_MAKE_OPTIONS,
  SITE_TRUCK_TRANSMISSION_TYPE_OPTIONS,
  SITE_TRUCK_UNIT_MODEL_CLASS_OPTIONS,
} from "../constants";

/**
 * Return abbreviation for meter types
 * @param {string} value
 */
export const abbreviateMeter = value => {
  value = value.toString().toLowerCase();

  switch (value) {
    case "hours":
      value = "hrs";
      break;
    case "kilometers":
      value = "km";
      break;
    case "miles":
      value = "mi";
      break;
    default:
      break;
  }

  return formatTitleCase(value);
};

/**
 * Return a user-friendly value for named account numbers
 * @param {string} source
 */
export const formatAccountNumber = source => {
  if (!source) {
    return "–";
  }

  if (!!source && !Array.isArray(source)) {
    source = `${source}`.split(",");
  }

  const formatted = source.reduce((acc, curr) => {
    const found = SITE_KEY_ACCOUNT_OPTIONS.find(
      option => option.value === curr
    );
    acc.push((!!found && `${found.label} (${found.value})`) || curr);
    return acc;
  }, []);

  return formatted.join(", ");
};

/**
 * Return user-friendly value for attachment name
 *
 * @param {string} name
 * @returns string
 */
export const formatAttachmentName = name => {
  switch (name) {
    case "brochure":
      return "Brochure";
    case "buildSheet":
      return "Build Sheet";
    case "inspectionReport":
      return "Inspection";
    case "productStatus":
      return "Product Status";
    case "serviceHistory":
      return "Service History";
    case "supportingInformation":
      return "Supporting Information";
    case "ta1":
      return "TA1";
    case "technicalData":
      return "Technical Data";
    case "undercarriageAssessment":
      return "Undercarriage";
    default:
      return name;
  }
};

/**
 * Return a user-friendly value for body type
 * @param {string} bodyType
 */
export const formatBodyType = bodyType => {
  if (!bodyType) {
    return "–";
  }

  if (!!bodyType && !Array.isArray(bodyType)) {
    bodyType = `${bodyType}`.split(",");
  }

  bodyType = bodyType.reduce((found, b) => {
    const foundBodyType = SITE_TRUCK_BODY_TYPE_OPTIONS.find(
      option => option.value === b
    );

    if (!!foundBodyType?.label) {
      found.push(foundBodyType.label);
      return found;
    }

    found.push(b);
    return found;
  }, []);

  return bodyType.join(", ");
};

/**
 * Return a user-friendly value for cab type
 * @param {string} cabType
 */
export const formatCabType = cabType => {
  if (!cabType) {
    return "–";
  }

  if (!!cabType && !Array.isArray(cabType)) {
    cabType = `${cabType}`.split(",");
  }

  cabType = cabType.reduce((found, c) => {
    const foundCabType = SITE_TRUCK_CAB_TYPE_OPTIONS.find(
      option => option.value === c
    );

    if (!!foundCabType?.label) {
      found.push(foundCabType.label);
      return found;
    }

    found.push(c);
    return found;
  }, []);

  return cabType.join(", ");
};

/**
 * Return a user-friendly value for engine make
 * @param {string} engineMake
 */
export const formatEngineMake = engineMake => {
  if (!engineMake) {
    return "–";
  }

  if (!!engineMake && !Array.isArray(engineMake)) {
    engineMake = `${engineMake}`.split(",");
  }

  engineMake = engineMake.reduce((found, m) => {
    const foundEngineMake = SITE_TRUCK_ENGINE_MAKE_OPTIONS.find(
      option => option.value === m
    );

    if (!!foundEngineMake?.label) {
      found.push(foundEngineMake.label);
      return found;
    }

    found.push(m);
    return found;
  }, []);

  return engineMake.join(", ");
};

/**
 * Return a user-friendly value for make
 * @param {string} make
 */
export const formatMake = make => {
  if (!make) {
    return "–";
  }

  if (!!make && !Array.isArray(make)) {
    make = `${make}`.split(",");
  }

  make = make.reduce((found, m) => {
    const foundTruckMake = SITE_TRUCK_MAKE_OPTIONS.find(
      option => option.value === m
    );

    if (!!foundTruckMake?.label) {
      found.push(foundTruckMake.label);
      return found;
    }

    const foundMake = SITE_MAKE_OPTIONS.find(option => option.value === m);

    if (!!foundMake?.label) {
      found.push(foundMake.label);
      return found;
    }

    found.push(m);
    return found;
  }, []);

  return make.join(", ");
};

/**
 * Return a user-friendly value for truck make
 * @param {string} make
 */
export const formatTruckMake = make => {
  if (!make) {
    return "–";
  }

  const found = SITE_TRUCK_MAKE_OPTIONS.find(option => option.value === make);

  return (!!found && found.label) || make;
};

/**
 * Return a user-friendly value for transimission type
 * @param {string} transmissionType
 */
export const formatTransmissionType = transmissionType => {
  if (!transmissionType) {
    return "–";
  }

  if (!!transmissionType && !Array.isArray(transmissionType)) {
    transmissionType = `${transmissionType}`.split(",");
  }

  transmissionType = transmissionType.reduce((found, t) => {
    const foundTransimissionType = SITE_TRUCK_TRANSMISSION_TYPE_OPTIONS.find(
      option => option.value === t
    );

    if (!!foundTransimissionType?.label) {
      found.push(foundTransimissionType.label);
      return found;
    }

    found.push(t);
    return found;
  }, []);

  return transmissionType.join(", ");
};

/**
 * Return a user-friendly value for unit model class
 * @param {string} unitModelClass
 */
export const formatUnitModelClass = unitModelClass => {
  if (!unitModelClass) {
    return "–";
  }

  if (!!unitModelClass && !Array.isArray(unitModelClass)) {
    unitModelClass = `${unitModelClass}`.split(",");
  }

  unitModelClass = unitModelClass.reduce((found, c) => {
    const foundUnitModelClass = SITE_TRUCK_UNIT_MODEL_CLASS_OPTIONS.find(
      option => option.value === c
    );

    if (!!foundUnitModelClass?.label) {
      found.push(foundUnitModelClass.label);
      return found;
    }

    found.push(c);
    return found;
  }, []);

  return unitModelClass.join(", ");
};

/**
 * Return a number with the specified precision
 * without trailing zeros
 * @param {string, number} value
 * @param {number} [precision=2]
 */
export const formatPrecision = (value, precision = 2, trim = true) => {
  if (typeof value === "string" || value instanceof String) {
    value = value.replace(/,/g, "");
  }

  value = parseFloat(value);
  value = value.toFixed(precision).toString();

  if (trim) {
    value = value.replace(/.00$/, "");
  }

  return value;
};

/**
 * Return a comma-formatted number
 * @param {number, string} value
 */
export const formatNumber = (value, precision = 2, trim = true) => {
  const number = formatPrecision(value, precision, trim);
  let parts = number.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
};

/**
 * Return a user-friendly value for rental status
 * @param {number, string} status
 */
export const formatRentalStatus = status => {
  status = parseInt(status, 10);

  if (isNaN(status) || !status) {
    return "Available";
  } else {
    return "Out on rent";
  }
};

/**
 * Return a user-friendly value for source division
 * @param {string} source
 */
export const formatSourceDivision = source => {
  if (!source) {
    return "–";
  }

  if (!!source && !Array.isArray(source)) {
    source = `${source}`.split(",");
  }

  const formatted = source.reduce((acc, curr) => {
    const found = SITE_SOURCE_OPTIONS.find(option => option.value === curr);
    acc.push((!!found && found.label) || formatTitleCase(curr));
    return acc;
  }, []);

  return formatted.join(", ");
};

/**
 * Return the original value for source division label
 * @param {string} source
 * @param {bool} returnArray
 */
export const unformatSourceDivision = (source, returnArray = false) => {
  if (!source) {
    return "";
  }

  if (!!source && !Array.isArray(source)) {
    source = `${source}`.split(",");
  }

  const formatted = source.reduce((acc, curr) => {
    curr = curr.toString().trim();
    const found = SITE_SOURCE_OPTIONS.find(option => option.label === curr);
    let unformatted = (!!found && found.value) || curr.toLowerCase();

    if (unformatted === "watertrucks") {
      unformatted = "waterTrucks";
    }
    acc.push(unformatted);
    return acc;
  }, []);

  return returnArray ? formatted : formatted.join(",");
};

/**
 * Return a user-friendly value for status
 * @param {string} status
 */
export const formatStatus = status => {
  if (!status) {
    return "–";
  }

  if (!!status && !Array.isArray(status)) {
    status = `${status}`.split(",");
  }

  const formatted = status.reduce((acc, curr) => {
    const found = SITE_STATUS_OPTIONS.find(option => option.value === curr);
    acc.push((!!found && found.label) || formatTitleCase(curr));
    return acc;
  }, []);

  return formatted.join(", ");
};

/**
 * Return a title-cased string
 * @param {string} value
 */
export const formatTitleCase = value =>
  value
    .toString()
    .toLowerCase()
    .split(" ")
    .map(word => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(" ");

/**
 * Return the first letter in a string upper-cased
 * @param {string} value
 */
export const formatUpperInitial = value =>
  value.toString().charAt(0).toUpperCase();

/**
 * Get base filename from path
 * @param {string} path
 */
export const getBaseName = path => path.toString().split(/[\\/]/).pop();

/**
 * Get file size from bytes
 * @param {number} bytes
 */
export const getFileSize = bytes => {
  const sizeKb = 1024;
  const sizeMb = sizeKb * sizeKb;
  const sizeGb = sizeKb * sizeMb;
  let formatted = bytes;

  if (bytes) {
    if (bytes >= sizeGb) {
      formatted = `${bytes / sizeGb} GB`;
    } else if (bytes >= sizeMb) {
      formatted = `${bytes / sizeMb} MB`;
    } else if (bytes >= sizeKb / 2) {
      formatted = `${bytes / sizeKb} KB`;
    } else if (bytes > 1) {
      formatted = `${bytes} bytes`;
    } else {
      formatted = `${bytes} byte`;
    }
  }

  return formatted;
};

/**
 * Get initial
 * @param {string} value
 */
export const getInitial = value => value.toString().charAt(0).toUpperCase();

/**
 * Remove protocol
 * @param {string} url
 */
export const removeProtocol = url => url.toString().replace(/^https?:\/\//, "");

/**
 * Sanitize for filename
 * @param {*} fragment
 */
export const sanitizeForFilename = fragment =>
  (fragment || "")
    .toString()
    .replace(/[^a-z0-9]/gi, "-")
    .toLowerCase();

export const parseMultiQuery = value =>
  decodeURIComponent(value.toString()).split(",");

/**
 * Parse array of numbers out of query for range field
 * @param {string} value
 */
export const parseRangeQuery = range =>
  decodeURIComponent(range.toString())
    .split(",")
    .slice(0, 2)
    .map(value => parseInt(value, 10));
/**
 * Parse phone number
 * @param {string} value
 */
export const formatTel = value =>
  value.replace(
    /^\+?(1)?[ \-.]?\(?(\d{3})\)?[ \-.]?(\d{3})[ \-.]?(\d{4})[^\d]*(\d*)$/,
    "$2-$3-$4"
  );

/**
 * convert string to lowercase and spaces to hyphens
 * @param {string} str
 */
export const urlify = str => {
  return str.replace(/\s+/g, "-").toLowerCase();
};
